import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { UserIcon, ShieldCheckIcon, CreditCardIcon, GiftIcon, LockIcon, FingerprintIcon, SmartphoneIcon, HelpCircleIcon, MessageCircleIcon, AlertTriangleIcon, FileTextIcon, InfoIcon, LogOutIcon, ChevronRightIcon } from 'lucide-react';
import { TopBar } from '../components/TopBar';
interface ProfileProps {
  navigate: (screen: string) => void;
}
export function Profile({
  navigate
}: ProfileProps) {
  const [isKycCompleted, setIsKycCompleted] = useState(false);
  useEffect(() => {
    const status = sessionStorage.getItem('kyc_status');
    if (status === 'completed' || status === 'hidden') {
      setIsKycCompleted(true);
    }
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  const menuGroups = [{
    title: 'Account',
    items: [{
      icon: UserIcon,
      label: 'Edit Profile',
      action: () => navigate('edit-profile')
    }, {
      icon: ShieldCheckIcon,
      label: 'KYC Verification',
      action: () => navigate('kyc'),
      badge: 'Tier 2'
    }, {
      icon: CreditCardIcon,
      label: 'Bank Accounts',
      action: () => navigate('bank-accounts')
    }, {
      icon: GiftIcon,
      label: 'Referral Program',
      action: () => navigate('referral-program')
    }]
  }, {
    title: 'Security',
    items: [{
      icon: LockIcon,
      label: 'Change PIN',
      action: () => navigate('change-pin')
    }, {
      icon: FingerprintIcon,
      label: 'Biometric Login',
      action: () => {},
      toggle: true
    }, {
      icon: SmartphoneIcon,
      label: 'Two-Factor Auth',
      action: () => navigate('two-factor-auth')
    }]
  }, {
    title: 'Support',
    items: [{
      icon: HelpCircleIcon,
      label: 'Help Center',
      action: () => navigate('help-center')
    }, {
      icon: MessageCircleIcon,
      label: 'Live Chat',
      action: () => navigate('live-chat')
    }, {
      icon: AlertTriangleIcon,
      label: 'Report a Problem',
      action: () => navigate('report-problem')
    }]
  }, {
    title: 'About',
    items: [{
      icon: FileTextIcon,
      label: 'Terms & Conditions',
      action: () => {}
    }, {
      icon: ShieldCheckIcon,
      label: 'Privacy Policy',
      action: () => {}
    }, {
      icon: InfoIcon,
      label: 'App Version',
      action: () => {},
      value: '2.1.0'
    }]
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="flex-1 overflow-y-auto pb-24 bg-slate-50" data-id="element-2546">
      <TopBar title="Profile" onNotificationClick={() => navigate('notifications')} data-id="element-2547" />

      <div className="px-5 pt-4 pb-6" data-id="element-2548">
        {/* Profile Header */}
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-6 text-center relative overflow-hidden" data-id="element-2549">
          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#0D3B3B]/5 to-transparent" data-id="element-2550"></div>

          <div className="w-20 h-20 rounded-full bg-[#0D3B3B] text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4 relative z-10 shadow-lg shadow-emerald-900/20 border-4 border-white" data-id="element-2551">
            AO
            <img src="https://cdn.magicpatterns.com/uploads/ai3GT46WXxVymwerHg44X4/default_mascott_-_green.png" alt="Mascot Badge" className="absolute -bottom-1 -right-1 w-8 h-8 object-contain bg-white rounded-full border-2 border-white shadow-sm p-0.5" data-id="element-2552" />
          </div>

          <h2 className="text-xl font-bold text-[#0D3B3B] mb-1" data-id="element-2553">
            Adaeze Okonkwo
          </h2>
          <p className="text-sm text-gray-500 mb-1" data-id="element-2554">adaeze@email.com</p>
          <p className="text-sm text-gray-500 font-medium" data-id="element-2555">+234 801 234 5678</p>
        </div>

        {/* KYC Status Banner */}
        <div onClick={() => !isKycCompleted && navigate('kyc')} className={`rounded-2xl p-5 text-white mb-8 shadow-lg transition-transform relative overflow-hidden ${isKycCompleted ? 'bg-[#0D3B3B] shadow-[#0D3B3B]/20' : 'bg-gradient-to-r from-emerald-500 to-emerald-600 shadow-emerald-500/20 cursor-pointer active:scale-95'}`} data-id="element-2556">
          <div className="absolute right-0 top-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-2557"></div>

          <div className="flex justify-between items-center relative z-10" data-id="element-2558">
            {isKycCompleted ? <>
                <div data-id="element-2559">
                  <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold mb-2 border border-white/20" data-id="element-2560">
                    <ShieldCheckIcon className="w-3.5 h-3.5 text-[#FFD700]" data-id="element-2561" />{' '}
                    Fully Verified
                  </div>
                  <p className="text-sm text-white/80 font-medium" data-id="element-2562">
                    All verification steps complete ✓
                  </p>
                </div>
                <div className="text-right" data-id="element-2563">
                  <span className="text-xs font-bold bg-[#FFD700] text-[#0D3B3B] px-3 py-1.5 rounded-lg shadow-sm" data-id="element-2564">
                    Completed
                  </span>
                </div>
              </> : <>
                <div data-id="element-2565">
                  <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold mb-2 border border-white/20" data-id="element-2566">
                    <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-2567" /> Tier 2 Verified
                  </div>
                  <p className="text-sm text-emerald-50 font-medium" data-id="element-2568">
                    BVN ✓ • ID ✓ • Address ⏳
                  </p>
                </div>
                <div className="text-right" data-id="element-2569">
                  <span className="text-xs font-bold bg-white text-emerald-600 px-3 py-1.5 rounded-lg shadow-sm" data-id="element-2570">
                    Upgrade
                  </span>
                </div>
              </>}
          </div>
        </div>

        {/* Menu Groups */}
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-6" data-id="element-2571">
          {menuGroups.map((group, gIdx) => <motion.div variants={itemVariants} key={gIdx} data-id="element-2572">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-2" data-id="element-2573">
                {group.title}
              </h3>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-2574">
                {group.items.map((item, iIdx) => <button key={iIdx} onClick={item.action} className={`w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${iIdx !== group.items.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-2575">
                    <div className="flex items-center gap-3" data-id="element-2576">
                      <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500" data-id="element-2577">
                        <item.icon className="w-4 h-4" data-id="element-2578" />
                      </div>
                      <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-2579">
                        {item.label}
                      </span>
                    </div>

                    <div className="flex items-center gap-2" data-id="element-2580">
                      {item.badge && <span className="text-[10px] font-bold bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded-md uppercase tracking-wider" data-id="element-2581">
                          {item.badge}
                        </span>}
                      {item.value && <span className="text-xs font-medium text-gray-400" data-id="element-2582">
                          {item.value}
                        </span>}
                      {item.toggle ? <div className="w-10 h-6 bg-emerald-500 rounded-full relative shadow-inner" data-id="element-2583">
                          <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow-sm" data-id="element-2584"></div>
                        </div> : <ChevronRightIcon className="w-4 h-4 text-gray-400" data-id="element-2585" />}
                    </div>
                  </button>)}
              </div>
            </motion.div>)}

          {/* Logout */}
          <motion.div variants={itemVariants} data-id="element-2586">
            <button onClick={() => navigate('welcome')} className="w-full bg-white rounded-2xl border border-rose-100 p-4 flex items-center justify-center gap-2 text-rose-500 font-bold shadow-sm hover:bg-rose-50 transition-colors active:scale-95" data-id="element-2587">
              <LogOutIcon className="w-5 h-5" data-id="element-2588" /> Log Out
            </button>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>;
}